import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import React, { useRef, useEffect, useCallback, useState } from 'react';
import SortableHeader, { SortDirection } from './sortable-header';
import styles from './styles';
import { ColumnSpec, DataTableProps } from './types';

interface SortOptions<T> {
    sortColumn: keyof T;
    sortDirection: SortDirection;
}

function DataTable<T>({ columns, data, loadMore, hasMore, handleRowClick, sortData }: DataTableProps<T>) {
    const tableEl = useRef<any>();
    const [sortOptions, setSortOptions] = useState<SortOptions<T>>();

    const endScrolled = () => {
        const { scrollHeight, offsetHeight, scrollTop } = tableEl.current;
        if (Math.round(scrollTop + offsetHeight) + 1 >= scrollHeight) {
            return true;
        }
        return false;
    };

    const scrollListener = useCallback(() => {
        if (hasMore && endScrolled()) {
            loadMore();
        }
    }, [hasMore, loadMore]);

    useEffect(() => {
        const tableRef = tableEl.current;
        tableRef.addEventListener('scroll', scrollListener);
        return () => {
            tableRef.removeEventListener('scroll', scrollListener);
        };
    }, [scrollListener]);

    const renderColumnHeaders = (columnSpec: ColumnSpec<T>[]) => (
        <TableRow>
            {columnSpec.map((cs, index) => (
                <SortableHeader
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    name={cs.name}
                    label={cs.title}
                    direction={
                        sortOptions?.sortColumn === cs.name ? sortOptions.sortDirection : SortDirection.Descending
                    }
                    onSort={(sortKey, direction) => {
                        setSortOptions({ sortColumn: sortKey as unknown as keyof T, sortDirection: direction });
                        if (sortData) {
                            sortData(sortKey, direction);
                        }
                    }}
                    sortVisible={cs.sortVisible}
                />
            ))}
        </TableRow>
    );

    const renderRow = (columnSpec: ColumnSpec<T>[], rowData: T) => {
        return (
            <TableRow
                id="table-row"
                onClick={() => {
                    if (handleRowClick) {
                        handleRowClick(rowData);
                    }
                }}
            >
                {columnSpec.map((colSpec) => (
                    <TableCell key={colSpec.name} sx={styles.rowLabel}>
                        {colSpec.cellRenderer != null ? colSpec.cellRenderer(rowData) : rowData[colSpec.cellProperty]}
                    </TableCell>
                ))}
            </TableRow>
        );
    };

    const renderBody = (columnSpec: ColumnSpec<T>[], rows: T[]) => {
        // eslint-disable-next-line react/no-array-index-key
        return rows.map((row, index) => <React.Fragment key={index}>{renderRow(columnSpec, row)}</React.Fragment>);
    };

    return (
        <TableContainer sx={styles.tableContainer} ref={tableEl} id="data-table-container">
            <Table id="data-table" stickyHeader>
                <TableHead id="data-table-head">{renderColumnHeaders(columns)}</TableHead>
                <TableBody id="data-table-body">{renderBody(columns, data)}</TableBody>
            </Table>
        </TableContainer>
    );
}

export default DataTable;
